import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogContentText,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Stack,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import Swal from "sweetalert2";
import { useAuth } from "../contexts/AuthContext";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";
import formatCurrency from "../shared/formatCurrency";

import {
  fetchGroupExpenses,
  createGroupExpense,
  approveGroupExpense,
  rejectGroupExpense,
} from "../services/expenseService";

const brand = "#800b33";
const brandHover = "#6c0a2b";



const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

export default function GroupExpensesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "sw" ? "sw-TZ" : "en-TZ";
  const { user } = useAuth();
  const role = user?.role || "Mwanachama";

  const [open, setOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ title: "", amount: "" });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expenseToApprove, setExpenseToApprove] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [expenseToReject, setExpenseToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const apiFilters = useMemo(() => {
    const params = {};
    if (statusFilter !== "ALL") params.status = statusFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (searchQuery) params.search = searchQuery;
    return params;
  }, [statusFilter, startDate, endDate, searchQuery]);

  useEffect(() => {
    const loadExpenses = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchGroupExpenses(apiFilters);
        setExpenses(data.results || data || []);
      } catch (err) {
        setError(err.message || t("Failed to load expenses."));
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    loadExpenses();
  }, [apiFilters, refreshKey]);

  const handleCreateExpense = async () => {
    try {
      await createGroupExpense({
        title: newExpense.title,
        amount: Number(newExpense.amount),
        group: user.group_id, // Add the group ID from the authenticated user
      });
      Swal.fire(t("Success"), t("Expense created and sent for approval."), "success");
      setNewExpense({ title: "", amount: "" });
      setOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const openConfirmationDialog = (expense) => {
    setExpenseToApprove(expense);
    setConfirmOpen(true);
  };
  const closeConfirmationDialog = () => {
    setExpenseToApprove(null);
    setConfirmOpen(false);
  };

  const handleConfirmApprove = async () => {
    if (!expenseToApprove) return;
    try {
      await approveGroupExpense(expenseToApprove.id, role);
      Swal.fire(t("Approved!"), t("The expense has been approved."), "success");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      closeConfirmationDialog();
    }
  };

  const openRejectDialog = (expense) => {
    setExpenseToReject(expense);
    setRejectDialogOpen(true);
  };
  const closeRejectDialog = () => {
    setExpenseToReject(null);
    setRejectDialogOpen(false);
    setRejectionReason(""); // Reset reason on close
  };

  const handleConfirmReject = async () => {
    if (!expenseToReject) return;
    try {
      await rejectGroupExpense(expenseToReject.id, rejectionReason);
      Swal.fire(t("Rejected!"), t("The expense has been rejected."), "success");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      closeRejectDialog();
    }
  };

  const expenseStats = useMemo(() => {
    const approvedExpenses = expenses.filter((e) => e.status === "APPROVED");
    const total = approvedExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
    const approved = approvedExpenses.length;
    return { total, approved };
  }, [expenses]);

  const handleResetFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("ALL");
  };

  const filteredExpenses = expenses; // Filtering is now done by the API


  const getStatusChip = (status) => {
    if (status === "APPROVED")
      return <Chip icon={<CheckCircleIcon />} label={t("Approved")} color="success" />;
    if (status.includes("PENDING"))
      return (
        <Chip
          icon={<HourglassTopIcon />}
          label={(status || "").replace("PENDING_", t("Pending ") + " ")}
          color="warning"
        />
      );
    return <Chip icon={<CancelIcon />} label={t("Rejected")} color="error" />;
  };

  const canApprove = (expense) => {
    if (!expense) return false;
    if (role === "Katibu" && expense.status === "PENDING_SECRETARY_APPROVAL") return true;
    if (role === "Muhasibu" && expense.status === "PENDING_ACCOUNTANT_APPROVAL") return true;
    return false;
  };

  const canReject = (expense) => {
    if (!expense) return false;
    // Secretary or accountant can reject in their respective stages
    if (role === "Katibu" && expense.status === "PENDING_SECRETARY_APPROVAL") return true;
    if (role === "Muhasibu" && expense.status === "PENDING_ACCOUNTANT_APPROVAL") return true;
    return false;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={3}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            {t("Group Expense Management")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Track and approve all group operational expenses")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField
            label={t("Search by Title")}
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label={t("Start Date")}
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("End Date")}
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>{t("Filter by Status")}</InputLabel>
            <Select
              value={statusFilter}
              label={t("Filter by Status")}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">{t("All Statuses")}</MenuItem>
              <MenuItem value="PENDING">{t("Pending")}</MenuItem>
              <MenuItem value="APPROVED">{t("Approved")}</MenuItem>
              <MenuItem value="REJECTED">{t("Rejected")}</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={handleResetFilters}
          >
            {t("Reset filters")}
          </Button>

          {role === "Mwenyekiti" && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
            >
              {t("New Expense")}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={0}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                boxShadow: 3,
                "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                transition: "all .25s ease",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("Total Expenses")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {formatCurrency(expenseStats.total, locale)}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} md={6}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
            <Card
              sx={{
                p: 2,
                borderRadius: 3,
                boxShadow: 3,
                "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                transition: "all .25s ease",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t("Approved Expenses")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {expenseStats.approved}
              </Typography>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* EXPENSE LIST */}
      <Grid container spacing={2}>
        {filteredExpenses.length > 0 && filteredExpenses.map((exp, idx) => (
          <Grid item xs={12} md={4} key={exp.id}>
            <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={idx + 2}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: 3,
                  "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
                  transition: "all .25s ease",
                  height: "100%", // Ensure cards have same height
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: brand }}>
                      {exp.title[0]}
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700}>{exp.title}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {t("Created:")} {new Date(exp.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Typography variant="h6" fontWeight={800}>
                    {formatCurrency(exp.amount, locale)}
                  </Typography>

                  <Box mt={1}>{getStatusChip(exp.status)}</Box>

                  {/* REJECTION REASON DISPLAY */}
                  {exp.status === "REJECTED" && exp.rejection_reason && (
                    <Box
                      mt={1.5}
                      p={1.5}
                      sx={{
                        bgcolor: "rgba(255, 82, 82, 0.08)",
                        borderRadius: 2,
                        border: "1px solid rgba(255, 82, 82, 0.2)",
                      }}
                    >
                      <Typography variant="caption" color="error.dark" sx={{ fontWeight: "bold", display: 'block' }}>
                        {t("Rejection Reason:")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{exp.rejection_reason}</Typography>
                    </Box>
                  )}

                  {/* APPROVAL ACTIONS */}
                  {canApprove(exp) && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{ borderColor: brand, color: brand }}
                        onClick={() => openConfirmationDialog(exp)}
                      >
                        {t("Approve Loan").split(" ")[0] /* Hack: Reusing "Approve" from "Approve Loan" or just "Approve" if added */}
                        {/* Better: just adding "Approve" key */}
                        {t("Approve", "Approve")}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={() => openRejectDialog(exp)}
                      >
                        {t("Reject", "Reject")}
                      </Button>
                    </Stack>
                  )}

                  {exp.status === "APPROVED" && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                      textAlign="center"
                      mt={2}
                    >
                      ✔ {t("Cost will be split equally across all members")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
        {loading && <Typography sx={{ p: 3 }}>{t("Loading expenses...")}</Typography>}
        {!loading && error && <Typography color="error" sx={{ p: 3 }}>{error}</Typography>}
        {!loading && !error && filteredExpenses.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 3, width: '100%', textAlign: 'center' }}>{t("No expenses found matching your filters.")}</Typography>
        )}
      </Grid>

      {/* CREATE EXPENSE MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("New Expense")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("Expense Title")}
            margin="normal"
            value={newExpense.title}
            onChange={(e) =>
              setNewExpense({ ...newExpense, title: e.target.value })
            }
          />
          <TextField
            fullWidth
            label={t("Amount (TZS)")}
            margin="normal"
            value={newExpense.amount ? Number(newExpense.amount).toLocaleString('en-US') : ''}
            onChange={(e) => {
              const val = e.target.value.replace(/,/g, "");
              if (!isNaN(val)) {
                setNewExpense({ ...newExpense, amount: val });
              }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: brand }}
            onClick={handleCreateExpense}
          >
            {t("Submit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* APPROVAL CONFIRMATION DIALOG */}
      <Dialog open={confirmOpen} onClose={closeConfirmationDialog}>
        <DialogTitle>{t("Confirm Approval")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span dangerouslySetInnerHTML={{
              __html: t("Are you sure you want to approve the expense: {{title}} for {{amount}}?", {
                title: `<strong>"${expenseToApprove?.title}"</strong>`,
                amount: `<strong>${formatCurrency(expenseToApprove?.amount || 0, locale)}</strong>`
              })
            }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmationDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmApprove}
            autoFocus
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* REJECTION DIALOG */}
      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t("Reject Expense")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("Please provide a reason for rejecting the expense: {{title}}", { title: expenseToReject?.title })}
            {/* <strong>"{expenseToReject?.title}"</strong>. */}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={t("Rejection Reason")}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={!rejectionReason}
          >
            {t("Confirm Rejection")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
